import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/admin/ImageUpload";
import { useSite } from "@/contexts/SiteContext";
import { ADMIN } from "@/constants/testIds";

export default function AdminSettings() {
  const { refresh } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!form) {
    return <div className="py-24 text-center text-xs tracking-widest uppercase text-foreground/60"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      await refresh();
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={ADMIN.settings} className="space-y-6 max-w-4xl">
      <div>
        <p className="section-label mb-1">Website Configuration</p>
        <h1 className="serif-display text-3xl md:text-4xl text-foreground">Site Settings</h1>
        <p className="mt-2 text-sm text-foreground/70">These values power the public website, header, footer, and hero.</p>
      </div>

      <Card title="Brand & Identity">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company Name"><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></Field>
          <Field label="Tagline"><Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></Field>
        </div>
        <ImageUpload label="Company Logo (PNG recommended)" value={form.logo} onChange={(u) => set("logo", u)} />
        <ImageUpload label="Favicon (small square icon)" value={form.favicon} onChange={(u) => set("favicon", u)} />
      </Card>

      <Card title="Announcement Bar">
        <Field label="Announcement Text"><Input value={form.announcement_text || ""} onChange={(e) => set("announcement_text", e.target.value)} /></Field>
        <label className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/40 cursor-pointer w-fit">
          <Switch checked={!!form.announcement_active} onCheckedChange={(c) => set("announcement_active", c)} />
          <span className="text-sm">Show announcement bar</span>
        </label>
      </Card>

      <Card title="Hero Section">
        <Field label="Hero Headline"><Input value={form.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} /></Field>
        <Field label="Hero Subheadline"><Textarea className="min-h-[80px]" value={form.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} /></Field>
        <ImageUpload label="Hero Background Image" value={form.hero_image} onChange={(u) => set("hero_image", u)} />
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Stat: Retailers"><Input value={form.stats_shops || ""} onChange={(e) => set("stats_shops", e.target.value)} /></Field>
          <Field label="Stat: Brands"><Input value={form.stats_brands || ""} onChange={(e) => set("stats_brands", e.target.value)} /></Field>
          <Field label="Stat: States"><Input value={form.stats_states || ""} onChange={(e) => set("stats_states", e.target.value)} /></Field>
          <Field label="Stat: Years"><Input value={form.stats_years || ""} onChange={(e) => set("stats_years", e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Contact & Address">
        <Field label="Address"><Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary Phone"><Input value={form.phone_primary || ""} onChange={(e) => set("phone_primary", e.target.value)} /></Field>
          <Field label="Secondary Phone"><Input value={form.phone_secondary || ""} onChange={(e) => set("phone_secondary", e.target.value)} /></Field>
          <Field label="Business Email"><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="WhatsApp Number (with country code)"><Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
          <Field label="Business Hours"><Input value={form.business_hours || ""} onChange={(e) => set("business_hours", e.target.value)} /></Field>
          <Field label="Google Maps URL"><Input value={form.google_maps_url || ""} onChange={(e) => set("google_maps_url", e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Social Media">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Instagram URL"><Input value={form.social_instagram || ""} onChange={(e) => set("social_instagram", e.target.value)} /></Field>
          <Field label="Facebook URL"><Input value={form.social_facebook || ""} onChange={(e) => set("social_facebook", e.target.value)} /></Field>
          <Field label="LinkedIn URL"><Input value={form.social_linkedin || ""} onChange={(e) => set("social_linkedin", e.target.value)} /></Field>
          <Field label="YouTube URL"><Input value={form.social_youtube || ""} onChange={(e) => set("social_youtube", e.target.value)} /></Field>
        </div>
      </Card>

      <div className="sticky bottom-0 bg-background/95 backdrop-blur py-4 border-t border-border/60 -mx-4 md:-mx-8 px-4 md:px-8 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold h-11 px-6 tracking-widest uppercase text-xs">
          {saving ? <><Loader2 size={13} className="animate-spin mr-2" /> Saving…</> : <><Save size={13} className="mr-2" /> Save Settings</>}
        </Button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="p-6 rounded-md border border-border/60 bg-card space-y-4">
      <p className="section-label">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs tracking-wider uppercase text-foreground/70">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
